// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useNavigate } from "@tanstack/react-router";
import type { PaginationState, SortingState } from "@tanstack/react-table";
import { Filter, MoreHorizontal, Pencil, RotateCcw, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api } from "@/api/client";
import {
  type SettingStoreEntry,
  useAddSettingForStoreMutation,
  useDeleteSettingForStoreMutation,
  useEditSettingForStoreMutation,
  useSettingDetailQuery,
  useSettingStoresQuery,
} from "@/api/hooks/use-settings";
import { DataTable, type DataTableColumnDef } from "@/components/data-table";
import { ChainLink } from "@/components/shared/chain-link";
import { FilterToggle } from "@/components/shared/filter-toggle";
import { RowId } from "@/components/shared/row-id";
import { StoreLink } from "@/components/shared/store-link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { RestoreDefaultDialog } from "@/features/settings/restore-default-dialog";
import { SettingValueCell } from "@/features/settings/setting-value-cell";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { downloadCsv } from "@/lib/csv";
import { decodeFilters, encodeFilters } from "@/lib/filter-params";
import { getDefaultPageSize } from "@/lib/preferences";
import { cn } from "@/lib/utils";
import { SettingDialog } from "./setting-dialog";

interface SettingStoresTabProps {
  settingName: string;
  initialFilters?: string;
}

const STATUS_STYLES: Record<string, string> = {
  store: "bg-[#FFF5D1] border-amber-200 text-foreground dark:bg-amber-950 dark:text-amber-300 dark:border-amber-900",
  chain: "bg-[#E3F1FF] border-blue-200 text-foreground dark:bg-blue-950 dark:text-blue-300 dark:border-blue-900",
};

function statusBadge(status: string, label: string) {
  return (
    <Badge variant="secondary" className={cn("font-normal", STATUS_STYLES[status])}>
      {label}
    </Badge>
  );
}

const FILTER_DEFAULTS = {
  search: "",
  onlyOverrides: true,
  page: 0,
  get pageSize() {
    return getDefaultPageSize();
  },
  sortId: "storeId",
  sortDesc: false,
};

export function SettingStoresTab({ settingName, initialFilters }: SettingStoresTabProps) {
  const { t } = useTranslation("back-office");
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(PAGE_PERMISSIONS.settingsStoresEdit);
  const { data: setting } = useSettingDetailQuery(settingName);
  const initial = useMemo(() => decodeFilters(initialFilters, FILTER_DEFAULTS), [initialFilters]);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initial.page,
    pageSize: initial.pageSize,
  });
  const [sorting, setSorting] = useState<SortingState>(
    initial.sortId !== "storeId" || initial.sortDesc ? [{ id: initial.sortId, desc: initial.sortDesc }] : [],
  );
  const [search, setSearch] = useState(initial.search);

  const [onlyOverrides, setOnlyOverrides] = useState(initial.onlyOverrides);
  const isFiltered = !!(search || !onlyOverrides);

  const [editEntry, setEditEntry] = useState<SettingStoreEntry | null>(null);
  const [restoreEntry, setRestoreEntry] = useState<SettingStoreEntry | null>(null);

  const syncUrl = useCallback(
    (overrides: Partial<typeof FILTER_DEFAULTS> = {}) => {
      const current = {
        search,
        onlyOverrides,
        page: pagination.pageIndex,
        pageSize: pagination.pageSize,
        sortId: sorting[0]?.id ?? "storeId",
        sortDesc: sorting[0]?.desc ?? false,
        ...overrides,
      };
      const encoded = encodeFilters(current, FILTER_DEFAULTS);
      navigate({
        to: "/settings/$settingName",
        params: { settingName },
        search: { t: "stores", ...(encoded ? { f: encoded } : {}) },
        replace: true,
      });
    },
    [search, onlyOverrides, pagination.pageIndex, pagination.pageSize, sorting, navigate, settingName],
  );

  useEffect(
    function synchronizeFiltersToUrl() {
      syncUrl();
    },
    [syncUrl],
  );

  const sortBy = (sorting[0]?.id ?? "storeId") as
    | "storeId"
    | "storeName"
    | "legibleName"
    | "chainLegibleName"
    | "value";
  const orderBy = sorting[0]?.desc ? ("desc" as const) : ("asc" as const);

  const { data, isLoading, refetch, isFetching } = useSettingStoresQuery({
    settingName,
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: search || undefined,
    sortBy,
    orderBy,
    onlyOverrides: onlyOverrides || undefined,
  });

  const handleExport = useCallback(async () => {
    const res = await api.GET("/v1/settings/{setting_name}/stores", {
      params: {
        path: { setting_name: settingName },
        query: {
          page: 1,
          limit: -1,
          search: search || undefined,
          sortBy,
          orderBy,
          "filter[onlyOverrides]": onlyOverrides || undefined,
        },
      },
    });
    const items = (res.data as unknown as { settings: SettingStoreEntry[] }).settings;
    downloadCsv(
      `setting-${settingName}-stores.csv`,
      ["Store ID", "Store", "Chain", "Value", "Status"],
      items.map((s) => [String(s.storeId), s.legibleName, s.chainLegibleName, s.value, s.status]),
    );
  }, [settingName, search, sortBy, orderBy, onlyOverrides]);

  const addMutation = useAddSettingForStoreMutation();
  const editMutation = useEditSettingForStoreMutation();
  const deleteMutation = useDeleteSettingForStoreMutation();

  async function onSubmit(value: string) {
    if (!editEntry || !setting) return;
    try {
      if (editEntry.overrideId) {
        await editMutation.mutateAsync({
          storeName: editEntry.storeName,
          body: { overrideId: editEntry.overrideId, newValue: value },
        });
      } else {
        await addMutation.mutateAsync({
          storeName: editEntry.storeName,
          body: { settingId: setting.settingId, newValue: value },
        });
      }
      toast.success(t("settings.list.toast.updated", { name: editEntry.legibleName }));
    } catch {
      // Error toast handled by API middleware
    }
  }

  async function handleRestore() {
    if (!restoreEntry?.overrideId) return;
    try {
      await deleteMutation.mutateAsync({
        storeName: restoreEntry.storeName,
        overrideId: restoreEntry.overrideId,
      });
      toast.success(t("settings.list.toast.restored", { name: restoreEntry.legibleName }));
      setRestoreEntry(null);
    } catch {
      // Error toast handled by API middleware
    }
  }

  const columns = useMemo<DataTableColumnDef<SettingStoreEntry>[]>(
    () => [
      {
        accessorKey: "storeId",
        header: t("settings.stores.columns.id"),
        size: 80,
        enableHiding: false,
        meta: { columnLabel: t("settings.stores.columns.id"), className: "pl-4", forceWidth: true, sticky: "left" },
        cell: ({ row }) => <RowId id={row.original.storeId} />,
      },
      {
        accessorKey: "legibleName",
        header: t("settings.stores.columns.store"),
        meta: { columnLabel: t("settings.stores.columns.store") },
        cell: ({ row }) => (
          <StoreLink
            storeName={row.original.storeName}
            legibleName={row.original.legibleName}
            className="font-medium"
          />
        ),
      },
      {
        accessorKey: "chainLegibleName",
        header: t("settings.stores.columns.chain"),
        meta: { columnLabel: t("settings.stores.columns.chain") },
        cell: ({ row }) =>
          row.original.chainName ? (
            <ChainLink chainName={row.original.chainName} legibleName={row.original.chainLegibleName} />
          ) : (
            (row.original.chainLegibleName ?? "—")
          ),
      },
      {
        accessorKey: "value",
        header: t("settings.stores.columns.value"),
        meta: { columnLabel: t("settings.stores.columns.value") },
        cell: ({ row }) => (
          <SettingValueCell
            value={row.original.value}
            fieldType={setting?.fieldType ?? "string"}
            name={setting?.name}
          />
        ),
      },
      {
        id: "status",
        header: t("settings.stores.columns.status"),
        enableSorting: false,
        size: 100,
        meta: { columnLabel: t("settings.stores.columns.status") },
        cell: ({ row }) => {
          const status = row.original.status;
          const label =
            status === "store"
              ? t("settings.status.store")
              : status === "chain"
                ? t("settings.status.chain")
                : status === "default"
                  ? t("settings.status.default")
                  : status;
          return statusBadge(status, label);
        },
      },
      {
        id: "actions",
        size: 50,
        meta: { forceWidth: true, sticky: "right" },
        enableSorting: false,
        enableHiding: false,
        cell: ({ row }) =>
          canEdit ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={-32} className="dropdown-overlap">
                <DropdownMenuItem onSelect={() => setEditEntry(row.original)}>
                  <Pencil className="h-4 w-4" />
                  {t("settings.list.actions.edit")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => setRestoreEntry(row.original)}
                  disabled={!row.original.overrideId || row.original.status !== "store"}
                >
                  <RotateCcw className="h-4 w-4" />
                  {t("settings.list.actions.restoreDefault")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null,
      },
    ],
    [t, setting?.fieldType, setting?.name, canEdit],
  );

  return (
    <div className="p-6">
      <DataTable
        storageKey="setting-stores"
        columns={columns}
        data={data?.settings ?? []}
        totalCount={data?.count ?? 0}
        isLoading={isLoading}
        pagination={pagination}
        onPaginationChange={setPagination}
        sorting={sorting}
        onSortingChange={setSorting}
        search={search}
        onSearchChange={setSearch}
        searchDebounceMs={1000}
        onRefresh={refetch}
        isRefreshing={isFetching}
        onExport={handleExport}
        searchPlaceholder={t("settings.stores.searchPlaceholder")}
        filters={
          <>
            <FilterToggle
              active={onlyOverrides}
              onChange={() => {
                setOnlyOverrides((v) => !v);
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
              icon={Filter}
              label={t("settings.list.filter.overridesOnly")}
            />
            {isFiltered && !onlyOverrides && (
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 text-muted-foreground hover:text-foreground"
                onClick={() => {
                  setSearch("");
                  setOnlyOverrides(true);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </>
        }
      />

      {setting && (
        <SettingDialog
          open={!!editEntry}
          onClose={() => setEditEntry(null)}
          settingName={setting.name}
          settingKey={setting.key}
          description={setting.description}
          fieldType={setting.fieldType}
          enumValues={setting.enumValues}
          defaultValue={setting.defaultValue}
          currentValue={editEntry?.value}
          onSave={onSubmit}
        />
      )}

      <RestoreDefaultDialog
        open={!!restoreEntry}
        onClose={() => setRestoreEntry(null)}
        onConfirm={handleRestore}
        settingName={restoreEntry?.legibleName}
        defaultValue={setting?.defaultValue}
        confirmDisabled={!canEdit || restoreEntry?.status !== "store"}
      />
    </div>
  );
}
